// src/components/compliance/RequirementsTab.tsx
//
// Manage compliance requirements. When enforcementMode='hard_block'
// is chosen, save requires typing the requirement name to confirm —
// a hard-block is a load-bearing policy and this dialog is the last
// line of defense against a mis-click.
//
// Gated by compliance.manage_requirements for writes.

import { useMemo, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  useGetComplianceRequirementsQuery,
  useCreateComplianceRequirementMutation,
  useUpdateComplianceRequirementMutation,
  useDeleteComplianceRequirementMutation,
  useGetComplianceCategoriesQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import Button from '../common/Button';
import { useHasCapability } from '../../services/centralContext';
import { EnforcementModeChip } from './Chips';
import {
  ENFORCEMENT_MODE_DESCRIPTION,
  ENFORCEMENT_MODE_LABEL,
  messageFromComplianceError,
} from './complianceDisplay';
import RequirementAssignmentsDrawer from './RequirementAssignmentsDrawer';
import type {
  ComplianceEnforcementMode,
  ComplianceRequirement,
} from '../../types/api';

interface RequirementsTabProps {
  workspaceId: string;
  onNavigate: NavigateFunction;
}

const ENFORCEMENT_MODES: ComplianceEnforcementMode[] = [
  'informational',
  'warning',
  'approval_required',
  'hard_block',
];

export default function RequirementsTab({ workspaceId, onNavigate: _onNavigate }: RequirementsTabProps) {
  const canManage = useHasCapability('compliance', 'manage_requirements');
  const canAssign = useHasCapability('compliance', 'assign');
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<ComplianceEnforcementMode | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ComplianceRequirement | null>(null);
  const [deleting, setDeleting] = useState<ComplianceRequirement | null>(null);
  const [assigning, setAssigning] = useState<ComplianceRequirement | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useGetComplianceRequirementsQuery({
    q: q || undefined,
    enforcementMode: mode || undefined,
    activeOnly,
    limit: 200,
  });
  const [deleteRequirement, deleteState] = useDeleteComplianceRequirementMutation();

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorState title="Could not load requirements" message={messageFromComplianceError(error)} onRetry={refetch} />;

  const rows = data?.rows ?? [];

  return (
    <section className="space-y-4" data-testid="compliance-requirements-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="req-search" className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <input
              id="req-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded border-gray-300 text-sm w-48"
              placeholder="Name…"
              data-testid="compliance-req-search"
            />
          </div>
          <div>
            <label htmlFor="req-mode" className="block text-xs font-medium text-gray-700 mb-1">Enforcement</label>
            <select
              id="req-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ComplianceEnforcementMode | '')}
              className="rounded border-gray-300 text-sm"
              data-testid="compliance-req-mode-filter"
            >
              <option value="">All</option>
              {ENFORCEMENT_MODES.map((m) => (
                <option key={m} value={m}>{ENFORCEMENT_MODE_LABEL[m]}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              data-testid="compliance-req-active-only"
            />
            Active only
          </label>
        </div>
        {canManage && (
          <Button onClick={() => setCreating(true)} data-testid="compliance-req-new-btn">
            New requirement
          </Button>
        )}
      </div>

      {banner && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {banner}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Enforcement</th>
              <th className="px-4 py-2">Validity</th>
              <th className="px-4 py-2">Lead days</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50" data-testid={`compliance-req-row-${row.id}`}>
                <td className="px-4 py-2 text-gray-900">
                  <div className="font-medium">{row.name}</div>
                  {row.description && <div className="text-xs text-gray-500 mt-0.5">{row.description}</div>}
                </td>
                <td className="px-4 py-2 text-gray-700">
                  {row.category?.name ?? '—'}
                </td>
                <td className="px-4 py-2"><EnforcementModeChip mode={row.enforcementMode} /></td>
                <td className="px-4 py-2 text-gray-700">{row.validityDays === null ? 'No expiry' : `${row.validityDays} days`}</td>
                <td className="px-4 py-2 text-gray-700">{row.renewalLeadDays ?? '—'}</td>
                <td className="px-4 py-2 text-xs">{row.isActive ? <span className="text-emerald-700">Active</span> : <span className="text-gray-500">Inactive</span>}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  {canAssign && (
                    <button
                      type="button"
                      onClick={() => setAssigning(row)}
                      className="text-xs text-indigo-700 hover:underline"
                      data-testid={`compliance-req-assign-${row.id}`}
                    >Assignments</button>
                  )}
                  {canManage && (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        className="text-xs text-indigo-700 hover:underline"
                        data-testid={`compliance-req-edit-${row.id}`}
                      >Edit</button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="text-xs text-red-700 hover:underline"
                        data-testid={`compliance-req-delete-${row.id}`}
                      >Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-sm text-gray-500">No requirements match your filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <RequirementFormDialog
          requirement={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {assigning && (
        <RequirementAssignmentsDrawer
          requirement={assigning}
          workspaceId={workspaceId}
          onClose={() => setAssigning(null)}
        />
      )}

      {deleting && (
        <DeleteRequirementDialog
          requirement={deleting}
          isDeleting={deleteState.isLoading}
          onConfirm={async () => {
            setBanner(null);
            try {
              await deleteRequirement(deleting.id).unwrap();
              setDeleting(null);
            } catch (e) {
              setBanner(messageFromComplianceError(e));
              setDeleting(null);
            }
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// RequirementFormDialog + hard-block confirmation
// ---------------------------------------------------------------------------

function RequirementFormDialog({
  requirement,
  onClose,
}: {
  requirement: ComplianceRequirement | null;
  onClose: () => void;
}) {
  const editing = !!requirement;
  const [name, setName] = useState(requirement?.name ?? '');
  const [categoryId, setCategoryId] = useState(requirement?.categoryId ?? '');
  const [description, setDescription] = useState(requirement?.description ?? '');
  const [requirementType, setRequirementType] = useState(requirement?.requirementType ?? '');
  const [enforcementMode, setEnforcementMode] = useState<ComplianceEnforcementMode>(
    requirement?.enforcementMode ?? 'warning',
  );
  const [validityDays, setValidityDays] = useState<string>(
    requirement?.validityDays !== null && requirement?.validityDays !== undefined
      ? String(requirement.validityDays)
      : '',
  );
  const [renewalLeadDays, setRenewalLeadDays] = useState<string>(
    requirement?.renewalLeadDays !== null && requirement?.renewalLeadDays !== undefined
      ? String(requirement.renewalLeadDays)
      : '30',
  );
  const [approvalRequired, setApprovalRequired] = useState(requirement?.approvalRequired ?? false);
  const [reviewRequired, setReviewRequired] = useState(requirement?.reviewRequired ?? false);
  const [isMandatory, setIsMandatory] = useState(requirement?.isMandatory ?? true);
  const [isActive, setIsActive] = useState(requirement?.isActive ?? true);
  const [err, setErr] = useState<string | null>(null);
  const [showHardBlockDialog, setShowHardBlockDialog] = useState(false);

  const { data: categories } = useGetComplianceCategoriesQuery({ includeInactive: false });
  const [create, createState] = useCreateComplianceRequirementMutation();
  const [update, updateState] = useUpdateComplianceRequirementMutation();
  const submitting = createState.isLoading || updateState.isLoading;

  // Whether the user is turning ON hard_block that wasn't already on.
  const isBecomingHardBlock =
    enforcementMode === 'hard_block' &&
    (!requirement || requirement.enforcementMode !== 'hard_block');

  const doSave = async () => {
    setErr(null);
    try {
      const payload = {
        categoryId,
        name: name.trim(),
        description: description || null,
        requirementType: requirementType || null,
        enforcementMode,
        validityDays: validityDays.trim() === '' ? null : Number(validityDays),
        renewalLeadDays: renewalLeadDays.trim() === '' ? null : Number(renewalLeadDays),
        approvalRequired,
        reviewRequired,
        isMandatory,
      };
      if (editing && requirement) {
        await update({ id: requirement.id, patch: { ...payload, isActive } }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onClose();
    } catch (e) {
      setErr(messageFromComplianceError(e));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isBecomingHardBlock) {
      setShowHardBlockDialog(true);
    } else {
      void doSave();
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="compliance-req-dialog-title"
      >
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto"
          data-testid="compliance-req-form"
        >
          <h2 id="compliance-req-dialog-title" className="text-lg font-semibold text-gray-900">
            {editing ? 'Edit requirement' : 'New requirement'}
          </h2>

          {err && (
            <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {err}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label htmlFor="req-name" className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input id="req-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded border-gray-300 text-sm" required />
            </div>

            <div>
              <label htmlFor="req-category" className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                id="req-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full rounded border-gray-300 text-sm"
                required
                data-testid="compliance-req-category-select"
              >
                <option value="" disabled>Choose…</option>
                {(categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="req-desc" className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea id="req-desc" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded border-gray-300 text-sm" />
            </div>

            <div>
              <label htmlFor="req-type" className="block text-xs font-medium text-gray-700 mb-1">Type (free text, optional)</label>
              <input id="req-type" value={requirementType ?? ''} onChange={(e) => setRequirementType(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
            </div>

            <div>
              <fieldset className="space-y-2" data-testid="compliance-req-enforcement-fieldset">
                <legend className="text-xs font-medium text-gray-700">Enforcement</legend>
                {ENFORCEMENT_MODES.map((m) => (
                  <label key={m} className={`flex gap-2 rounded border p-2 cursor-pointer ${enforcementMode === m ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-200'}`}>
                    <input
                      type="radio"
                      name="req-enforcement"
                      value={m}
                      checked={enforcementMode === m}
                      onChange={() => setEnforcementMode(m)}
                      data-testid={`compliance-req-mode-${m}`}
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{ENFORCEMENT_MODE_LABEL[m]}</div>
                      <div className="text-xs text-gray-500">{ENFORCEMENT_MODE_DESCRIPTION[m]}</div>
                    </div>
                  </label>
                ))}
              </fieldset>
              {enforcementMode === 'hard_block' && (
                <div role="alert" className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="compliance-req-hardblock-banner">
                  <strong>Hard block</strong> will refuse work for anyone missing or with expired evidence. You'll be asked to type the requirement name to confirm on save.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="req-validity" className="block text-xs font-medium text-gray-700 mb-1">Validity days (blank = no expiry)</label>
                <input id="req-validity" type="number" min={0} value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
              </div>
              <div>
                <label htmlFor="req-lead" className="block text-xs font-medium text-gray-700 mb-1">Renewal lead days</label>
                <input id="req-lead" type="number" min={0} value={renewalLeadDays} onChange={(e) => setRenewalLeadDays(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={approvalRequired} onChange={(e) => setApprovalRequired(e.target.checked)} /> Approval required</label>
              <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={reviewRequired} onChange={(e) => setReviewRequired(e.target.checked)} /> Review required</label>
              <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} /> Mandatory</label>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active</label>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      </div>

      {showHardBlockDialog && (
        <HardBlockConfirmDialog
          requirementName={name.trim() || 'this requirement'}
          onCancel={() => setShowHardBlockDialog(false)}
          onConfirm={async () => {
            setShowHardBlockDialog(false);
            await doSave();
          }}
        />
      )}
    </>
  );
}

function HardBlockConfirmDialog({
  requirementName,
  onCancel,
  onConfirm,
}: {
  requirementName: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === requirementName.trim();

  const handle = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-hardblock-title"
      data-testid="compliance-hardblock-dialog"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4">
        <h3 id="compliance-hardblock-title" className="text-lg font-semibold text-red-800">
          Enable Hard Block?
        </h3>
        <p className="text-sm text-gray-700">
          Turning on <strong>hard_block</strong> will refuse work for anyone with missing or expired evidence for this requirement. This is the strongest enforcement level and should be reserved for legal / regulatory controls.
        </p>
        <p className="text-sm text-gray-700">
          Type <span className="font-mono bg-gray-100 px-1 rounded">{requirementName}</span> to confirm:
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full rounded border-gray-300 text-sm"
          data-testid="compliance-hardblock-input"
          aria-label="Type the requirement name to confirm"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={handle}
            disabled={!matches || busy}
            data-testid="compliance-hardblock-confirm"
          >
            {busy ? 'Saving…' : 'Enable Hard Block'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeleteRequirementDialog({
  requirement,
  isDeleting,
  onConfirm,
  onCancel,
}: {
  requirement: ComplianceRequirement;
  isDeleting: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-req-del-title"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl space-y-4">
        <h3 id="compliance-req-del-title" className="text-lg font-semibold text-gray-900">
          Delete "{requirement.name}"?
        </h3>
        <p className="text-sm text-gray-600">
          You can only delete a requirement that has no active assignments and no live documents. Archive them first if needed.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button type="button" onClick={onConfirm} disabled={isDeleting} data-testid="compliance-req-del-confirm">
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
